// Copyright ©️ 2026 eVolve MEP, LLC

import { createContext, useContext, useState, useMemo, useCallback } from 'react';

import type { BendCreatorNewBend } from '@evolvemep/api-ts';
import { useDisclosure, useListState } from '@mantine/hooks';
import { v4 as uuid } from 'uuid';

import { isNil, isNotNil } from 'helpers/isNotNil';
import { useGeneralContext } from 'helpers/useGeneralContext';

import { useBendCreatorFormContext } from './useBendCreatorFormContext';
import { useBendCreatorValidationContext } from './useBendCreatorValidationContext';

declare const CartBendDraftIdSymbol: unique symbol;
export type CartBendDraftId = string & { readonly [CartBendDraftIdSymbol]: unique symbol };

export type CartBend = BendCreatorNewBend & {
  draftId: CartBendDraftId;
};

type BendCreatorCartContextType = {
  /** When not null, an item within the cart that is being edited. */
  selectedItem: CartBend | null;
  /** Sets the currently selected item within the cart. */
  onSelect: (draftId: CartBendDraftId | null) => void;
  cart: CartBend[];
  submitting: boolean;
  onSubmitCart: () => void;
  onCartSubmitDone: () => void;
  append: (item: BendCreatorNewBend) => void;
  update: (draftId: CartBendDraftId, item: BendCreatorNewBend) => void;
  remove: (draftId: CartBendDraftId) => void;
  restoringBend: boolean;
};

const BendCreatorCartContext = createContext<BendCreatorCartContextType | undefined>(undefined);

type BendCreatorCartContextProviderProps = {
  defaultSelected?: CartBendDraftId | null;
  initialValue?: CartBend[];
  children: React.ReactNode;
};

/** Provides a list state and its handlers for a list of CartBend items */
export const BendCreatorCartContextProvider = ({
  defaultSelected,
  initialValue,
  children,
}: BendCreatorCartContextProviderProps) => {
  const [submitting, { open: onSubmitCart, close: onCartSubmitDone }] = useDisclosure(false);
  const [selected, setSelected] = useState<CartBendDraftId | null>(defaultSelected ?? null);
  const [cart, cartUpdateHandlers] = useListState<CartBend>(initialValue ?? []);
  const [restoringBend, { open: onRestoreBend, close: onRestoreBendDone }] = useDisclosure(false);

  const { setValues } = useBendCreatorFormContext();
  const { resetLastValidation, validateBend } = useBendCreatorValidationContext();

  const selectedItem = useMemo<CartBend | null>(() => {
    if (isNotNil(selected)) {
      const index = cart.findIndex((item) => item.draftId === selected);
      return cart[index] ?? null;
    }
    return null;
  }, [selected, cart]);

  const onSelect = useCallback<BendCreatorCartContextType['onSelect']>(
    async (draftId: CartBendDraftId | null) => {
      if (isNil(draftId)) {
        setSelected(null);
        return;
      }
      onRestoreBend();
      try {
        const bend = cart.find((item) => item.draftId === draftId);
        if (isNil(bend)) return;
        setSelected(draftId);
        const { draftId: _, ybcRows, ...formValues } = bend;

        // Trigger validation after selecting a bend
        resetLastValidation();
        setValues(
          isNotNil(ybcRows) ? { ...formValues, ybcRows: ybcRows.map((row) => ({ ...row, key: uuid() })) } : formValues,
        );
        await validateBend();
      } finally {
        onRestoreBendDone();
      }
    },
    [cart, setSelected, setValues, resetLastValidation, validateBend, onRestoreBend, onRestoreBendDone],
  );

  const append = useCallback<BendCreatorCartContextType['append']>(
    (item: BendCreatorNewBend) => {
      if (submitting) return;
      cartUpdateHandlers.append({ ...item, draftId: uuid() as CartBendDraftId });
    },
    [submitting, cartUpdateHandlers],
  );

  const update = useCallback<BendCreatorCartContextType['update']>(
    (draftId: CartBendDraftId, item: BendCreatorNewBend) => {
      if (submitting) return;
      const index = cart.findIndex((cartItem) => cartItem.draftId === draftId);
      const prev = cart[index];
      if (isNil(prev)) return;
      cartUpdateHandlers.setItem(index, { ...item, draftId });
    },
    [submitting, cart, cartUpdateHandlers],
  );

  const remove = useCallback<BendCreatorCartContextType['remove']>(
    (draftId: CartBendDraftId) => {
      if (submitting) return;
      const index = cart.findIndex((item) => item.draftId === draftId);
      if (index === -1) return;
      cartUpdateHandlers.remove(index);
    },
    [submitting, cart, cartUpdateHandlers],
  );

  return (
    <BendCreatorCartContext.Provider
      value={{
        selectedItem,
        onSelect,
        cart,
        submitting,
        onSubmitCart,
        onCartSubmitDone,
        append,
        update,
        remove,
        restoringBend,
      }}
    >
      {children}
    </BendCreatorCartContext.Provider>
  );
};

export const useBendCreatorCartContext = () => useGeneralContext(BendCreatorCartContext, 'BendCreatorCartContext');

export const useOptionalBendCreatorCartContext = () => useContext(BendCreatorCartContext);
